import { defineStore } from 'pinia';
import { DateTime } from 'luxon';

const COUPON_LOCAL_STORAGE_KEY = 'coupon';
const MAX_TIMEOUT_MS = 2147483647;

let startedBetsTimeout = null;

const getFromLocalStorage = () => {
  const coupon = localStorage.getItem(COUPON_LOCAL_STORAGE_KEY);
  return coupon ? JSON.parse(coupon) : [];
}

const getBetEventStart = (bet) => {
  const base = DateTime.fromSQL(bet.match_date || '');
  if (!base.isValid) {
    return null;
  }
  const time = DateTime.fromSQL(String(bet.hour || '').trim());
  return time.isValid ? base.set({ hour: time.hour, minute: time.minute, second: 0, millisecond: 0 }) : base;
}

export const getCouponStatus = (coupon) => {
  if (coupon.bets.some(bet => bet.result === '-1')) return 'lost';
  if (coupon.bets.some(bet => bet.settled === '0')) return 'unsettled';
  if (coupon.bets.every(bet => bet.settled === '1' && bet.result === '0')) return 'returned';
  return 'won';
}

export const getCouponTotalRate = (coupon) => {
  return coupon.bets.reduce((acc, bet) => {
    return bet.result !== '-2' ? acc * parseFloat(bet.rate) : acc;
  }, 1);
}

export const getTopWinningCoupons = (coupons) => {
  return coupons
    .filter(coupon => getCouponStatus(coupon) === 'won' && getCouponTotalRate(coupon) >= 5.0)
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 3);
}

export const useCouponStore = defineStore('Coupon', {
  state: () => ({
    couponLikes: {
      couponID: 0,
      likes: 0
    },
    coupons: [],
    isCouponAdded: false,
    limitReached: false,
    addedBets: getFromLocalStorage()
  }),
  actions: {
    getLimitReached() {
      return axios.post(window.WP.ajaxUrl, {
        endpoint: 'api_check_if_user_reached_coupon_limit',
        user_id: window.WP.user.id,
      })
      .then(response => {
        if (response.data.success) {
          console.log(response);
          this.limitReached = response.data.data.coupon_limit_reached;
        }
      });
    },
    setCoupons(coupons) {
      this.coupons = coupons;
    },
    setLikes(data) {
      this.couponLikes = data;
    },
    deleteCoupon(deletedCouponID) {
      this.coupons = this.coupons.filter((coupon) => parseInt(coupon.coupon_id) !== deletedCouponID)
    },
    getLikesByCouponID(couponID) {
	    return this.couponLikes.filter((coupon) => coupon.couponID === couponID)[0].likes;
    },
    addLikeByCouponID(couponID) {
      this.couponLikes.filter((coupon) => coupon.couponID === couponID)[0].likes++;
    },
    removeLikeByCouponID(couponID) {
      this.couponLikes.filter((coupon) => coupon.couponID === couponID)[0].likes--;
    },
    saveToLocalStorage() {
      localStorage.setItem(COUPON_LOCAL_STORAGE_KEY, JSON.stringify(this.addedBets))
    },
    removeCouponFromLocalStorage() {
      localStorage.removeItem(COUPON_LOCAL_STORAGE_KEY);
    },
    removeStartedBets() {
      const now = DateTime.now();
      const notStartedBets = this.addedBets.filter(bet => {
        const start = getBetEventStart(bet);
        return !start || start > now;
      });
      if (notStartedBets.length !== this.addedBets.length) {
        this.addedBets = notStartedBets;
        this.saveToLocalStorage();
      }
    },
    scheduleStartedBetsRemoval() {
      clearTimeout(startedBetsTimeout);
      this.removeStartedBets();
      const starts = this.addedBets.map(getBetEventStart).filter(Boolean);
      if (starts.length > 0) {
        const ms = DateTime.min(...starts).diffNow().milliseconds;
        startedBetsTimeout = setTimeout(() => this.scheduleStartedBetsRemoval(), Math.min(ms, MAX_TIMEOUT_MS));
      }
    }
  },
});